<template>
  <div id="raised-card">
    <div class="exchange-container">
      <SmallLoader v-if="loading" />
      <template v-else>
        <!-- Exchange Table Header -->
        <div v-if="companiesRaised.length > 0" class="exchange-table">
          <div class="table-header">
            <div class="th-token">Token</div>
            <div class="th-price">Price</div>
            <div class="th-change">24h Change</div>
            <div class="th-stats">Details</div>
            <div class="th-volume"></div>
            <div class="th-action"></div>
          </div>
          
          <!-- Exchange Rows -->
          <router-link
            v-for="(token, index) in companiesRaised"
            :key="index"
            :to="{
              name: 'TokenOverview',
              params: { symbol: token.symbol, id: token._id },
            }"
            class="exchange-row"
            :id="token.symbol"
          >
            <!-- Token Info -->
            <div class="cell-token">
              <div class="token-logo">
                <img 
                  :src="token.image" 
                  :alt="token.name" 
                  loading="lazy"
                  @error="handleImageError"
                />
              </div>
              <div class="token-details">
                <span class="token-name">{{ token.name }}</span>
                <div class="token-meta">
                  <span class="token-symbol">{{ token.symbol }}</span>
                  <DealTypeBadge :badge="viewOf(token).badge" size="sm" />
                  <ShariaBadge :token="token" size="sm" />
                </div>
              </div>
            </div>

            <!-- Price -->
            <div class="cell-price">
              <span class="price-value">{{ formatAmountToDollar(token.price?.exchange || 0, priceDecimals(token), currency(token)) }}</span>
              <span v-if="priceUnit(token)" class="price-unit">{{ priceUnit(token) }}</span>
            </div>

            <!-- Change -->
            <div class="cell-change">
              <div class="price-change-badge" :class="getChangeClass(token)">
                <svg v-if="parseFloat(token.percentage) >= 0" class="change-icon" viewBox="0 0 12 12" fill="none">
                  <path d="M6 9V3M6 3L3 6M6 3L9 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <svg v-else class="change-icon" viewBox="0 0 12 12" fill="none">
                  <path d="M6 3V9M6 9L3 6M6 9L9 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span>{{ formatPercentage(token.percentage) }}%</span>
              </div>
            </div>

            <!-- The two figures that matter for this kind of deal: a note's rate and tenor, a
                 metal's unit and past month, a stock's NGX close and range. -->
            <div v-for="(pill, i) in viewOf(token).pills.slice(0, 2)" :key="pill.k" :class="i === 0 ? 'cell-stats' : 'cell-volume'">
              <span class="stat-label">{{ pill.k }}</span>
              <span class="stat-value" :class="{ highlight: pill.tone === 'up', down: pill.tone === 'down' }">{{ pill.v }}</span>
            </div>
            <div v-for="n in Math.max(2 - viewOf(token).pills.length, 0)" :key="'pad-' + n" class="cell-empty"></div>

            <!-- Action -->
            <div class="cell-action">
              <span class="action-btn">
                Trade
                <svg class="action-icon" viewBox="0 0 16 16" fill="none">
                  <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
            </div>
          </router-link>
        </div>

        <!-- Empty State -->
        <div class="empty-container" v-else>
          <EmptyState>
            <template v-slot:message>
              <p class="empty-text" v-if="hasSearchQuery">
                No company with that name exists
              </p>
              <p class="empty-text" v-else>
                No companies listed on the exchange yet
              </p>
            </template>
          </EmptyState>
        </div>
      </template>

      <!-- Pagination -->
      <div v-if="$route.name !== 'OverviewDetails' && pages > 0" class="pagination-container">
        <Pagination
          :totalPage="pages"
          :currentPage="page"
          :maxVisible="5"
          @pageChange="gotoPage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Pagination from "@/components/navigation/Pagination";
import EmptyState from "@/components/notification/EmptyState";
import { formatAmountToDollar, getCurrencySymbol, formatCompactAmount } from "@/utils/helpers.js";
import DealTypeBadge from "@/components/general/DealTypeBadge.vue";
import ShariaBadge from "@/components/general/ShariaBadge.vue";
import { DealKinds, listingView, priceDecimals } from "@/utils/trade-view";
import { mapActions, mapState } from "vuex";

export default {
  name: "RaisedCard",

  components: { Pagination, EmptyState, DealTypeBadge, ShariaBadge },
  data() {
    return {
      loading: false,
    };
  },

  computed: {
    ...mapState({
      companiesRaised: (state) => state.TokenModule.raisedTokens,
      page: (state) => state.TokenModule.raisedPage,
      pages: (state) => state.TokenModule.raisedPages,
    }),
    views() {
      return new Map((this.companiesRaised || []).map((token) => [token._id, listingView(token)]));
    },
  },
  props: {
    hasSearchQuery: {
      type: Boolean,
      required: false,
      default: () => {
        return false;
      },
    },
  },

  mounted() {
    if (!this.hasSearchQuery) {
      this.getDetails();
    }
  },

  methods: {
    ...mapActions("TokenModule", ["getCompaniesRaised"]),

    formatAmountToDollar,

    currency(token) {
      return token.currency || 'USD';
    },

    viewOf(token) {
      return this.views.get(token._id) || listingView(token);
    },

    priceDecimals,

    // What the price is for, where it is not obviously a share: a gram of a metal, a fund unit.
    priceUnit(token) {
      const view = this.viewOf(token);
      if (view.kind === DealKinds.COMMODITY) return view.hero?.unit || "per token";
      if (view.kind === DealKinds.FUND) return "per unit";
      if (view.kind === DealKinds.LISTED) return "per share";
      return "";
    },

    getChangeClass(token) {
      const change = parseFloat(token.percentage) || 0;
      return change >= 0 ? 'positive' : 'negative';
    },

    formatPercentage(value) {
      const num = parseFloat(value) || 0;
      return num >= 0 ? `+${num.toFixed(2)}` : num.toFixed(2);
    },

    getCurrencySymbol,

    formatCompact(value, curr = 'USD') {
      return formatCompactAmount(parseFloat(value) || 0, curr);
    },

    handleImageError(e) {
      e.target.src = require("@/assets/img/token-icon.svg");
    },

    async getDetails() {
      this.loading = true;
      let limit = 6;
      if (this.$route.name !== "OverviewDetails") limit = 12;
      const payload = { page: 1, limit: limit };
      await this.getCompaniesRaised(payload);
      this.loading = false;
    },

    async gotoPage(page) {
      this.loading = true;
      let limit;
      if (this.$route.name !== "OverviewDetails") limit = 12;
      const payload = {
        page: page,
        limit: limit,
      };
      await this.getCompaniesRaised(payload);
      this.loading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
#raised-card {
  .exchange-container {
    width: 100%;
  }

  .exchange-table {
    width: 100%;
    background: #FFFFFF;
    border: 1px solid #E5E7EB;
    border-radius: 12px;
    overflow: hidden;
  }

  // Table Header
  .table-header {
    display: none;
    padding: 14px 20px;
    background: #F9FAFB;
    border-bottom: 1px solid #E5E7EB;
    font-size: 11px;
    font-weight: 500;
    color: #6B7280;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    @media screen and (min-width: 768px) {
      display: grid;
      grid-template-columns: 2fr 1fr 1fr 1fr 1fr 120px;
      gap: 16px;
      align-items: center;
    }
  }

  // Exchange Row
  .exchange-row {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px 20px;
    text-decoration: none;
    border-bottom: 1px solid #F3F4F6;
    transition: background 0.15s ease;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: #F9FAFB;

      .action-btn {
        background: #3B82F6;
        color: #FFFFFF;
      }
    }

    @media screen and (min-width: 768px) {
      display: grid;
      grid-template-columns: 2fr 1fr 1fr 1fr 1fr 120px;
      gap: 16px;
      align-items: center;
    }
  }

  // Token Cell
  .cell-token {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .token-logo {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    overflow: hidden;
    background: #F9FAFB;
    border: 1px solid #E5E7EB;
    flex-shrink: 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .token-details {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  .token-name {
    font-size: 15px;
    font-weight: 500;
    color: #111827;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .token-meta {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .token-symbol {
    font-size: 13px;
    color: #3B82F6;
    font-weight: 500;
  }


  // Price Cell
  .cell-price {
    @media screen and (max-width: 767px) {
      display: flex;
      justify-content: space-between;
      align-items: center;

      &::before {
        content: 'Price';
        font-size: 12px;
        color: #6B7280;
      }
    }
  }

  .price-value {
    font-size: 15px;
    font-weight: 600;
    color: #111827;
  }

  .price-unit {
    display: block;
    font-size: 12px;
    color: #6B7280;

    @media screen and (max-width: 767px) {
      display: none;
    }
  }

  // Change Cell
  .cell-change {
    @media screen and (max-width: 767px) {
      display: flex;
      justify-content: space-between;
      align-items: center;

      &::before {
        content: '24h Change';
        font-size: 12px;
        color: #6B7280;
      }
    }
  }

  .price-change-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 5px 10px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;

    &.positive {
      background: #ECFDF5;
      color: #059669;
    }

    &.negative {
      background: #FEF2F2;
      color: #DC2626;
    }
  }

  .change-icon {
    width: 12px;
    height: 12px;
  }

  // Detail Cells: each carries its own label, since what it shows depends on the deal.
  .cell-stats,
  .cell-volume {
    display: flex;
    flex-direction: column;
    gap: 2px;

    @media screen and (max-width: 767px) {
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
    }
  }

  .cell-empty {
    @media screen and (max-width: 767px) {
      display: none;
    }
  }

  .stat-label {
    font-size: 11px;
    color: #6B7280;

    @media screen and (max-width: 767px) {
      font-size: 12px;
    }
  }

  .stat-value {
    font-size: 14px;
    font-weight: 500;
    color: #374151;

    &.highlight {
      color: #059669;
    }

    &.down {
      color: #DC2626;
    }
  }

  // Action Cell
  .cell-action {
    display: flex;
    justify-content: flex-end;

    @media screen and (max-width: 767px) {
      justify-content: stretch;
      margin-top: 4px;
    }
  }

  .action-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 10px 20px;
    background: #F3F4F6;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    color: #374151;
    transition: all 0.15s ease;

    @media screen and (max-width: 767px) {
      width: 100%;
    }
  }

  .action-icon {
    width: 14px;
    height: 14px;
    transition: transform 0.15s ease;
  }

  .exchange-row:hover .action-icon {
    transform: translateX(3px);
  }

  // Empty State
  .empty-container {
    padding: 64px 24px;
    text-align: center;
    background: #FFFFFF;
    border-radius: 12px;
    border: 1px solid #E5E7EB;
  }

  .empty-text {
    color: #6B7280;
    font-size: 14px;
  }

  // Pagination
  .pagination-container {
    display: flex;
    justify-content: center;
    margin-top: 32px;
  }
}
</style>
  